import { CITY } from '@city'
import { useEffect } from 'react'
import type { ReactElement, ReactNode } from 'react'
import styles from './LegalLayout.module.css'

/**
 * The three legal documents (Privacy / Terms / Cookie).
 */
export type LegalPageKey = 'privacy' | 'terms' | 'cookie'

interface LegalLink {
  readonly href: string
  readonly label: string
}

/**
 * Canonical route + human label for each legal document. Kept
 * locale-LESS (as the prior in-body cross-links were): nginx / the SPA
 * `RootLocaleRedirect` resolves the path to the visitor's locale. Used
 * for the header brand and the footer cross-links.
 */
const LEGAL_LINKS: Record<LegalPageKey, LegalLink> = {
  privacy: { href: '/privacy-policy', label: 'Privacy Policy' },
  terms: { href: '/terms-and-conditions', label: 'Terms and Conditions' },
  cookie: { href: '/cookie-policy', label: 'Cookie Policy' },
}

const PAGE_ORDER: readonly LegalPageKey[] = ['privacy', 'terms', 'cookie']

/**
 * Standalone, scrollable shell for the legal pages.
 *
 * The map SPA locks the document (`html, body { overflow: hidden }` +
 * JS-sized `#root { position: absolute }` for the WebGL canvas — see
 * `src/index.css`), so long legal copy could not scroll on mobile and
 * the App Store reviewer could not read the policy the paywall links to.
 * This layout adds a `legal-page` class to `<html>` on mount (removed on
 * unmount) that overrides the lock to a normal scrollable document
 * (`overflow-y: auto`, `min-height: 100dvh`, `position: static`) — the
 * same escape-hatch pattern the visual-fixture routes already use
 * (`html.visual-fixture-active`). See the `html.legal-page` block in
 * `src/index.css`.
 *
 * Navigation chrome (brand, back-to-map, cross-links) uses plain
 * `<a href>` so the layout carries no router dependency — legal pages are
 * a separate reading surface from the map, and a full navigation back to
 * the map is the correct affordance.
 */
export function LegalLayout({
  current,
  children,
}: {
  current: LegalPageKey
  children: ReactNode
}): ReactElement {
  useEffect(() => {
    const root = document.documentElement
    root.classList.add('legal-page')
    return () => {
      root.classList.remove('legal-page')
    }
  }, [])

  const crossLinks = PAGE_ORDER.filter((key) => key !== current).map((key) => LEGAL_LINKS[key])

  return (
    <div className={styles.page} data-testid="legal-layout">
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <a className={styles.brand} href="/">
            {CITY.wordmarkLong}
          </a>
          <a className={styles.back} href="/">
            <span aria-hidden="true">←</span> Back to map
          </a>
        </div>
      </header>

      <main className={styles.content}>{children}</main>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <nav className={styles.footerLinks} aria-label="Legal">
            {crossLinks.map((link) => (
              <a key={link.href} href={link.href}>
                {link.label}
              </a>
            ))}
          </nav>
          <p className={styles.footerMeta}>
            Questions? <a href="mailto:support@mmthstudio.com">support@mmthstudio.com</a>
          </p>
        </div>
      </footer>
    </div>
  )
}
